// ============================================================================
// AUTO-LAYOUT MENU - Menu de opções de organização automática
// ============================================================================

import React, { useState } from 'react';
import { 
  LayoutGrid, 
  AlignHorizontalJustifyStart,
  AlignVerticalJustifyStart,
  AlignLeft,
  AlignRight,
  AlignStartVertical,
  AlignEndVertical,
  Workflow,
  Minimize2,
  ArrowRightLeft,
  ArrowUpDown,
  ChevronDown,
  Wand2,
  Layers,
} from 'lucide-react';
import { 
  autoLayoutCards, 
  alignLeft, 
  alignRight, 
  alignTop, 
  alignBottom,
  centerHorizontal,
  centerVertical,
  LayoutStrategy,
  layoutBySwimlanes,
  SwimLaneGroupBy,
  SwimLaneInfo,
} from '../utils/autoLayoutEngine';
import { CardData } from '../types';
import { SwimlanesModal } from './SwimlanesModal';

interface AutoLayoutMenuProps {
  /** Cards atualmente selecionados */
  selectedCards: CardData[];
  /** Todos os cards do canvas */
  allCards: CardData[];
  /** Callback para atualizar cards */
  onUpdateCards: (cards: CardData[]) => void;
  /** Callback quando raias são aplicadas (envia definições das raias) */
  onSwimlanesApplied?: (lanes: SwimLaneInfo[]) => void;
  /** Número de cards selecionados */
  selectionCount: number;
  /** Se o menu está habilitado */
  disabled?: boolean;
}

interface LayoutOption {
  id: LayoutStrategy | 'align-left' | 'align-right' | 'align-top' | 'align-bottom' | 'center-h' | 'center-v';
  label: string;
  icon: React.ReactNode;
  description: string;
  minCards?: number;
}

const LAYOUT_OPTIONS: LayoutOption[] = [
  { 
    id: 'flow', 
    label: 'Por Fluxo', 
    icon: <Workflow size={16} />,
    description: 'Organiza seguindo as conexões',
    minCards: 2
  },
  { 
    id: 'horizontal', 
    label: 'Linha Horizontal', 
    icon: <AlignHorizontalJustifyStart size={16} />,
    description: 'Alinha em uma linha',
    minCards: 2
  },
  { 
    id: 'vertical', 
    label: 'Coluna Vertical', 
    icon: <AlignVerticalJustifyStart size={16} />,
    description: 'Alinha em uma coluna',
    minCards: 2
  },
  { 
    id: 'grid', 
    label: 'Grade', 
    icon: <LayoutGrid size={16} />,
    description: 'Distribui em matriz',
    minCards: 2
  },
  { 
    id: 'compact', 
    label: 'Compactar', 
    icon: <Minimize2 size={16} />,
    description: 'Remove espaços vazios',
    minCards: 2
  },
  { 
    id: 'distribute-h', 
    label: 'Distribuir ↔', 
    icon: <ArrowRightLeft size={16} />,
    description: 'Espaçamento horizontal uniforme',
    minCards: 3
  },
  { 
    id: 'distribute-v', 
    label: 'Distribuir ↕', 
    icon: <ArrowUpDown size={16} />,
    description: 'Espaçamento vertical uniforme',
    minCards: 3
  },
];

const ALIGN_OPTIONS: LayoutOption[] = [
  { 
    id: 'align-left', 
    label: 'Alinhar à Esquerda', 
    icon: <AlignLeft size={16} />,
    description: 'Mesmo X esquerdo',
    minCards: 2
  },
  { 
    id: 'align-right', 
    label: 'Alinhar à Direita', 
    icon: <AlignRight size={16} />,
    description: 'Mesmo X direito',
    minCards: 2
  },
  { 
    id: 'align-top', 
    label: 'Alinhar ao Topo', 
    icon: <AlignStartVertical size={16} />,
    description: 'Mesmo Y superior',
    minCards: 2
  },
  { 
    id: 'align-bottom', 
    label: 'Alinhar à Base', 
    icon: <AlignEndVertical size={16} />,
    description: 'Mesmo Y inferior',
    minCards: 2
  },
];

export const AutoLayoutMenu: React.FC<AutoLayoutMenuProps> = ({
  selectedCards,
  allCards,
  onUpdateCards,
  onSwimlanesApplied,
  selectionCount,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSwimlanesModal, setShowSwimlanesModal] = useState(false);

  const handleLayout = (optionId: LayoutOption['id']) => {
    let updatedCards: CardData[];

    switch (optionId) {
      case 'align-left':
        updatedCards = alignLeft(selectedCards);
        break;
      case 'align-right':
        updatedCards = alignRight(selectedCards);
        break;
      case 'align-top':
        updatedCards = alignTop(selectedCards);
        break;
      case 'align-bottom':
        updatedCards = alignBottom(selectedCards);
        break;
      case 'center-h':
        updatedCards = centerHorizontal(selectedCards);
        break;
      case 'center-v':
        updatedCards = centerVertical(selectedCards);
        break;
      default:
        const result = autoLayoutCards(selectedCards, allCards, {
          strategy: optionId as LayoutStrategy,
          keepInitialFixed: true,
          snapToGrid: true,
        });
        updatedCards = result.cards;
    }

    // Mesclar com todos os cards
    const selectedIds = new Set(selectedCards.map(c => c.id));
    const mergedCards = allCards.map(card => {
      if (selectedIds.has(card.id)) {
        const updated = updatedCards.find(c => c.id === card.id);
        return updated || card;
      }
      return card;
    });

    onUpdateCards(mergedCards);
    setIsOpen(false);
  };

  const handleApplySwimlanes = (groupBy: SwimLaneGroupBy) => {
    const result = layoutBySwimlanes(selectedCards, allCards, { groupBy });
    if (result.cards.length > 0) {
      // Mesclar com todos os cards
      const updatedIds = new Set(result.cards.map(c => c.id));
      const mergedCards = allCards.map(card => {
        if (updatedIds.has(card.id)) {
          const updated = result.cards.find(c => c.id === card.id);
          return updated || card;
        }
        return card;
      });
      onUpdateCards(mergedCards);
      // Notificar parent com definições das raias para renderizar overlay
      onSwimlanesApplied?.(result.lanes);
    }
  };

  const canUseOption = (option: LayoutOption) => {
    return selectionCount >= (option.minCards || 2);
  };

  if (disabled || selectionCount < 2) {
    return null;
  }

  return (
    <div className="relative">
      {/* Botão Principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 transition-colors shadow-sm"
        title="Organizar automaticamente"
      >
        <Wand2 size={14} />
        <span>Organizar</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Menu Dropdown */}
      {isOpen && (
        <>
          {/* Overlay para fechar */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div 
            className="absolute bottom-full left-0 mb-2 w-64 bg-white rounded shadow-xl border border-gray-200 z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Organizar {selectionCount} cards
                </span>
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="text-xs text-purple-600 hover:text-purple-800"
                >
                  {showAdvanced ? 'Menos opções' : 'Mais opções'}
                </button>
              </div>
            </div>

            {/* Layout Options */}
            <div className="p-1">
              <div className="text-xs font-medium text-gray-400 px-2 py-1">
                Layout
              </div>
              {LAYOUT_OPTIONS.map(option => (
                <button
                  key={option.id}
                  onClick={() => handleLayout(option.id)}
                  disabled={!canUseOption(option)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                    canUseOption(option)
                      ? 'hover:bg-purple-50 text-gray-700'
                      : 'opacity-50 cursor-not-allowed text-gray-400'
                  }`}
                >
                  <span className="text-purple-600">{option.icon}</span>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{option.label}</div>
                    {showAdvanced && (
                      <div className="text-xs text-gray-400">{option.description}</div>
                    )}
                  </div>
                  {!canUseOption(option) && (
                    <span className="text-xs text-gray-400">
                      {option.minCards}+ cards
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Align Options */}
            {showAdvanced && (
              <div className="p-1 border-t border-gray-100">
                <div className="text-xs font-medium text-gray-400 px-2 py-1">
                  Alinhamento
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {ALIGN_OPTIONS.map(option => (
                    <button
                      key={option.id}
                      onClick={() => handleLayout(option.id)}
                      disabled={!canUseOption(option)}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors ${
                        canUseOption(option)
                          ? 'hover:bg-gray-100 text-gray-700'
                          : 'opacity-50 cursor-not-allowed text-gray-400'
                      }`}
                      title={option.description}
                    >
                      <span className="text-gray-500">{option.icon}</span>
                      <span className="text-xs">{option.label.replace('Alinhar ', '')}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Action - Auto Flow */}
            <div className="p-2 border-t border-gray-100 bg-purple-50">
              <button
                onClick={() => handleLayout('flow')}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                <Wand2 size={14} />
                Auto-Organizar por Fluxo
              </button>
            </div>

            {/* Swimlanes */}
            <div className="p-2 border-t border-gray-100">
              <button
                onClick={() => { setShowSwimlanesModal(true); setIsOpen(false); }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors text-sm font-medium"
              >
                <Layers size={14} />
                Converter em Raias
              </button>
            </div>
          </div>
        </>
      )}

      {/* Modal de Swimlanes */}
      <SwimlanesModal
        isOpen={showSwimlanesModal}
        onClose={() => setShowSwimlanesModal(false)}
        onApply={handleApplySwimlanes}
        cards={allCards}
      />
    </div>
  );
};

export default AutoLayoutMenu;
